import Image from "next/image";
import Link from "next/link";
import { type JSX } from "react";

import { MainThemesLayout } from "./MainThemesLayout";
import { SecondaryThemesLayout } from "./SecondaryThemesLayout";
import { LicensedProductsLayout } from "./LicensedProdutsLayout";

export type SectionKey = "masculino" | "feminino" | "infantil" | "temas" | "licenciados";

/** Qual painel está aberto no header: busca, uma seção ou nenhum. */
export type ActiveFlyout = "busca" | SectionKey | null;

export interface HeaderSection {
  key: SectionKey;
  name: string;
  imageUrl: string;
  /** Seções "principais" exibem o card de CTA "Crie a sua camiseta" ao lado dos links. */
  hasCreateCard: boolean;
  layout: JSX.Element;
}

export const HEADER_SECTIONS: HeaderSection[] = [
  {
    key: "masculino",
    name: "Masculino",
    imageUrl: "/images/header/masculino.png",
    hasCreateCard: true,
    layout: <MainThemesLayout theme="masculino" />,
  },
  {
    key: "feminino",
    name: "Feminino",
    imageUrl: "/images/header/feminino.png",
    hasCreateCard: true,
    layout: <MainThemesLayout theme="feminino" />,
  },
  {
    key: "infantil",
    name: "Infantil",
    imageUrl: "/images/header/infantil.png",
    hasCreateCard: true,
    layout: <MainThemesLayout theme="infantil" />,
  },
];

/** Card "Crie a sua camiseta" com a imagem da seção. */
const CreateProductCard = ({ imageUrl }: { imageUrl: string }): JSX.Element => (
  <div
    className={
      "relative mt-3 flex min-h-[10rem] w-full flex-col justify-center overflow-hidden rounded-2xl " +
      "border border-base-border bg-base-gray-opacity-5 p-3 lg:mt-0 lg:w-[19rem] lg:min-w-[19rem]"
    }
  >
    <Image src={imageUrl} fill sizes="304px" alt="" className="pointer-events-none object-cover object-[75%_20%]" />
    {/* Degradê branco à esquerda para manter o texto legível sobre a foto */}
    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,#fff_30%,rgba(255,255,255,0)_75%)]" />

    <div className="relative max-w-[10.5rem]">
      <b className="block text-lg">Crie a sua camiseta</b>
      <p className="mb-2 mt-1 text-sm">Escolha o blank, envie sua arte e receba em casa.</p>

      <Link
        href="/customizar-produto"
        className="inline-flex h-4 items-center rounded-full bg-[#000] px-2 text-sm font-bold text-base-white"
      >
        Começar a criar
      </Link>
    </div>
  </div>
);

/**
 * Retorna o conteúdo do painel da seção ativa (links + imagem/card), ou null.
 * @param activeFlyout - painel atualmente aberto no header
 */
export const getCurrentSectionLayout = (activeFlyout: ActiveFlyout): JSX.Element | null => {
  const activeSection = HEADER_SECTIONS.find((section) => section.key === activeFlyout);

  if (!activeSection) {
    return null;
  }

  if (activeSection.hasCreateCard) {
    return (
      <div className="flex flex-col lg:flex-row lg:items-stretch lg:gap-5">
        <div className="w-full lg:grow">{activeSection.layout}</div>
        <CreateProductCard imageUrl={activeSection.imageUrl} />
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col items-center lg:mt-0 lg:flex-row lg:items-start lg:gap-5">
      <Image src={activeSection.imageUrl} height={265} width={280} alt="" className="lg:h-[14.25rem] lg:w-auto" />

      <div className="w-full lg:min-h-[14.25rem] lg:grow">{activeSection.layout}</div>
    </div>
  );
};
